import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  AreaChart, 
  Area,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ResponsiveContainer,
  Cell 
} from 'recharts';
import { 
  FiDollarSign, 
  FiTrendingUp, 
  FiCreditCard, 
  FiActivity,
  FiArrowUp,
  FiArrowDown
} from 'react-icons/fi';
import './DashboardFinancas.css';

// Chart colors matching the army green theme
const CHART_COLORS = [
  '#3E5F56', // army green
  '#7B8D42', // lighter army green
  '#A8B479', // old silver light
  '#6B7B3A', // variant 1
  '#8FA351', // variant 2
  '#5C6E31', // variant 3
  '#9CAF69', // variant 4
  '#2B403A'  // dark army green
];

const DashboardFinancas = () => {
  const { userData } = useAuth();
  const navigate = useNavigate();
  
  // Permission check
  const hasPermission = userData?.permissions?.includes('Master') || 
                       userData?.permissions?.includes('Diretoria') ||
                       userData?.permissions?.includes('Financeiro');

  // State
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  const months = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  // Currency formatter
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Percentage formatter
  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="label">{label}</p>
          {payload.map((entry, index) => {
            const isCurrency = ['totalConsolidado', 'totalRevenue', 'totalPrevisto', 'previsto', 'consolidado', 'projetado', 'totalAmount', 'totalValue'].includes(entry.dataKey);
            return (
              <p key={index} style={{ color: entry.color }}>
                {entry.name}: {isCurrency ? formatCurrency(entry.value) : entry.value}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : 'http://localhost:5000';
      
      const response = await fetch(
        `${API_URL}/api/dashboard/finance?month=${selectedMonth}&year=${selectedYear}`,
        { 
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao carregar dados');
      }

      const data = await response.json();
      setDashboardData(data.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      alert('Erro ao carregar dados: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasPermission) {
      alert('Você não tem permissão para acessar esta página.');
      navigate('/painel');
      return;
    }
    
    fetchDashboardData();
  }, [selectedMonth, selectedYear, hasPermission, navigate]);

  if (!hasPermission) {
    return null;
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="dashboard-error">
        <p>Nenhum dado disponível</p>
      </div>
    );
  }

  const { overviewCards, monthlyEvolution, revenueByClient, revenueByService, receivablesAging, projectedCashFlow } = dashboardData;

  return (
    <div className="dashboard-financas">
      {/* Header with month/year selectors */}
      <div className="dashboard-header">
        <h2>Dashboard Financeiro</h2>
        <div className="date-selector">
          <select 
            value={selectedMonth} 
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
          >
            {months.map((month, index) => (
              <option key={index} value={index}>{month}</option>
            ))}
          </select>
          <select 
            value={selectedYear} 
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="overview-cards">
        {/* Receita Total Card */}
        <div className="overview-card">
          <h3>
            <FiDollarSign />
            Receita Total
          </h3>
          <div className="card-content">
            <div className="stat-value">{formatCurrency(overviewCards.receitaTotal.consolidado)}</div>
            <div className="stat-help-text">
              de {formatCurrency(overviewCards.receitaTotal.previsto)} previsto
            </div>
            <div className="stat-help-text">
              Taxa de Realização: {formatPercentage(overviewCards.receitaTotal.taxaRealizacao)}
            </div>
          </div>
        </div>

        {/* Fluxo de Caixa Card */}
        <div className="overview-card">
          <h3>
            <FiActivity />
            Fluxo de Caixa Mensal
          </h3>
          <div className="card-content">
            <div className="stat-value">{formatCurrency(overviewCards.fluxoCaixa.saldo)}</div>
            <div className="stat-help-text">
              Entradas: {formatCurrency(overviewCards.fluxoCaixa.entradas)}
            </div>
            <div className="stat-help-text">
              Saídas: {formatCurrency(overviewCards.fluxoCaixa.saidas)}
            </div>
          </div>
        </div>

        {/* Contas a Receber Card */}
        <div className="overview-card">
          <h3>
            <FiCreditCard />
            Contas a Receber
          </h3>
          <div className="card-content">
            <div className="stat-value">{formatCurrency(overviewCards.contasReceber.totalPendente)}</div>
            <div className="stat-help-text error">
              Vencidas: {formatCurrency(overviewCards.contasReceber.vencidas)} ({overviewCards.contasReceber.vencidasCount})
            </div>
            <div className="stat-help-text">
              A vencer: {formatCurrency(overviewCards.contasReceber.aVencer)}
            </div>
          </div>
        </div>

        {/* Performance Card */}
        <div className="overview-card">
          <h3>
            <FiTrendingUp />
            Performance Financeira
          </h3>
          <div className="card-content">
            <div className="stat-value">
              <span className={`stat-arrow ${overviewCards.performance.comparativoMesAnterior >= 0 ? 'up' : 'down'}`}>
                {overviewCards.performance.comparativoMesAnterior >= 0 ? <FiArrowUp /> : <FiArrowDown />}
              </span>
              {formatPercentage(Math.abs(overviewCards.performance.comparativoMesAnterior))}
            </div>
            <div className="stat-help-text">
              vs. mês anterior ({formatCurrency(overviewCards.performance.mesAnteriorConsolidado)})
            </div>
            <div className="stat-help-text">
              YTD: {formatCurrency(overviewCards.performance.crescimentoYtd)}
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="charts-section">
        {/* Monthly Evolution Chart */}
        <div className="chart-container">
          <h3>Evolução Financeira Mensal</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyEvolution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" angle={-45} textAnchor="end" height={80} />
              <YAxis tickFormatter={(value) => formatCurrency(value).replace('R$', '')} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="previsto" 
                stroke={CHART_COLORS[0]} 
                name="Previsto"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="consolidado" 
                stroke={CHART_COLORS[1]} 
                name="Consolidado"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue by Client Chart */}
        <div className="chart-container">
          <h3>Receita por Cliente (Top 10)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={revenueByClient}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="clienteName" angle={-45} textAnchor="end" height={100} interval={0} />
              <YAxis tickFormatter={(value) => formatCurrency(value).replace('R$', '')} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="totalRevenue" fill={CHART_COLORS[0]} name="Consolidado" />
              <Bar dataKey="totalPrevisto" fill={CHART_COLORS[2]} name="Previsto" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue by Service Chart */}
        <div className="chart-container">
          <h3>Receita por Serviço</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={revenueByService}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => `${entry._id}: ${formatCurrency(entry.totalValue)}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="totalValue"
              >
                {revenueByService.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Aging Chart */}
        <div className="chart-container">
          <h3>Aging de Contas a Receber</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={receivablesAging}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="range" />
              <YAxis tickFormatter={(value) => formatCurrency(value).replace('R$', '')} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="totalAmount" fill={CHART_COLORS[3]} name="Valor Total" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Cash Flow Projection */}
        <div className="chart-container">
          <h3>Fluxo de Caixa Projetado</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={projectedCashFlow}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={(value) => formatCurrency(value).replace('R$', '')} />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="projetado" 
                stroke={CHART_COLORS[4]} 
                fill={CHART_COLORS[4]} 
                fillOpacity={0.6}
                name="Projetado"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default DashboardFinancas;